import React, { useState, useEffect } from 'react';
import { Loader2, Save, Link2, UserPlus, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { API } from '../../utils/kitchenTasks';
import { formatApiError } from '../../services/api';

const ROLES = [['collaborator', 'Collaborateur'], ['chef', 'Chef'], ['admin', 'Direction (admin)'], ['super_admin', 'Super-administrateur']];
const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-900 text-sm';

// Compte de connexion associé à une fiche membre (ou utilisateur direct depuis l'administration)
const MemberAccountPanel = ({ member, user: directUser, open, onOpenChange, onChanged, allPermissions = [] }) => {
  const { hasPermission, user: me } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('collaborator');
  const [active, setActive] = useState(true);
  const [custom, setCustom] = useState([]);
  const [linkEmail, setLinkEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = (a) => { setAccount(a); setRole(a?.role || 'collaborator'); setActive(a ? a.active !== false : true); setCustom(a?.custom_permissions || []); };
    if (directUser) { init(directUser); return; }
    setLoading(true);
    axios.get(`${API}/admin/users`).then(r => init((r.data.data || []).find(u => u.member_id === member?.id || u.team_member?.id === member?.id) || null)).catch(() => init(null)).finally(() => setLoading(false));
  }, [open, member, directUser]);

  const save = async () => {
    if (!account || busy) return;
    setBusy(true);
    try {
      const payload = {};
      if (role !== account.role) payload.role = role;
      if (active !== (account.active !== false)) payload.active = active;
      if (JSON.stringify(custom) !== JSON.stringify(account.custom_permissions || [])) payload.custom_permissions = custom;
      if (!Object.keys(payload).length) { toast.info('Aucune modification'); return; }
      const r = await axios.patch(`${API}/admin/users/${account.id}`, payload);
      setAccount({ ...account, ...r.data.data }); toast.success('Compte mis à jour'); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const link = async () => {
    if (!member || busy) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/members/link`, { member_id: member.id, email: linkEmail }); toast.success('Compte associé'); onChanged?.(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const invite = async () => {
    if (!member || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/teams/${member.id}/invite`, null, { params: linkEmail ? { email: linkEmail } : {} });
      const d = r.data?.data || {};
      if (d.email_sent) toast.success(`Invitation envoyée par email à ${d.email}`);
      else { toast.info(d.email ? 'Email non envoyé (Resend non configuré) — transmettez le lien manuellement' : 'Lien d\'invitation créé'); if (d.invite_link) window.prompt('Lien d\'invitation à transmettre :', d.invite_link); }
      onChanged?.();
    }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const alignEmail = async () => {
    if (!member || !account || busy) return;
    setBusy(true);
    try {
      await axios.put(`${API}/teams/${member.id}`, { email: member.email, sync_account_email: true });
      setAccount({ ...account, email: member.email }); toast.success(`Adresse de connexion mise à jour : ${member.email}`); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const canRoles = hasPermission('manage_roles');
  const protectedTarget = account && (account.role === 'super_admin' || account.role === 'admin') && me?.role !== 'super_admin';
  const status = !account ? 'Aucun compte' : account.active === false ? 'Désactivé' : 'Actif';
  const memberEmail = (member?.email || '').trim().toLowerCase();
  const emailMismatch = !!(account && member && memberEmail && memberEmail !== account.email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="member-account-panel">
        <DialogHeader><DialogTitle className="text-gray-900">Compte de connexion — {member?.name || directUser?.name}</DialogTitle><DialogDescription>Rôle, statut du compte et permissions effectives.</DialogDescription></DialogHeader>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-neon-pink" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-gray-400 uppercase font-bold">Email du compte</p><p className="text-gray-900" data-testid="account-email">{account?.email || '—'}</p></div>
              <div><p className="text-xs text-gray-400 uppercase font-bold">Statut</p><p className={`font-semibold ${status === 'Actif' ? 'text-emerald-700' : status === 'Désactivé' ? 'text-red-700' : 'text-amber-700'}`} data-testid="account-status">{status}</p></div>
              {member && <><div><p className="text-xs text-gray-400 uppercase font-bold">Poste principal</p><p className="text-gray-900">{member.station || '—'}</p></div><div><p className="text-xs text-gray-400 uppercase font-bold">Postes secondaires</p><p className="text-gray-900">{(member.secondary_stations || []).join(', ') || '—'}</p></div></>}
            </div>
            {emailMismatch && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-2" data-testid="account-email-mismatch">
                <p className="text-sm text-amber-900"><strong>Emails différents :</strong> la fiche indique <span className="font-mono">{memberEmail}</span> mais le compte se connecte avec <span className="font-mono">{account.email}</span>.</p>
                {hasPermission('manage_team') && !protectedTarget && (
                  <Button variant="outline" onClick={alignEmail} disabled={busy} className="w-full h-10 rounded-full border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="account-align-email-btn">
                    {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Mail size={14} className="mr-1" />}Utiliser {memberEmail} comme adresse de connexion
                  </Button>
                )}
              </div>
            )}
            {!account ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="account-missing">
                <p className="text-sm text-amber-800">Ce membre n'a pas encore de compte de connexion : il apparaît dans la brigade mais ne peut pas se connecter.</p>
                {hasPermission('manage_team') && <>
                  <Input type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="email du compte existant ou à inviter" className={fieldCls} data-testid="account-link-email" />
                  <div className="flex gap-2"><Button variant="outline" onClick={link} disabled={busy || !linkEmail} className="flex-1 h-11 rounded-full" data-testid="account-link-btn"><Link2 size={14} className="mr-1" />Associer un compte existant</Button><Button onClick={invite} disabled={busy} className="flex-1 h-11 rounded-full bg-gray-900 text-white" data-testid="account-invite-btn"><UserPlus size={14} className="mr-1" />Inviter</Button></div>
                </>}
              </div>
            ) : (
              <>
                <div><Label className="text-gray-700">Rôle</Label>
                  <select value={role} onChange={(e) => setRole(e.target.value)} disabled={!canRoles || protectedTarget} className={`mt-1 block w-full px-3 border ${fieldCls}`} data-testid="account-role-select">
                    {ROLES.filter(([v]) => v !== 'super_admin' || me?.role === 'super_admin').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {!canRoles && <p className="text-xs text-gray-400 mt-1">Seul un super-administrateur peut modifier les rôles.</p>}
                  {protectedTarget && <p className="text-xs text-red-600 mt-1">Compte administrateur protégé.</p>}
                </div>
                <label className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2.5"><span className="text-sm text-gray-800">Compte actif</span><Switch checked={active} onCheckedChange={setActive} disabled={protectedTarget || !hasPermission('manage_team')} data-testid="account-active-switch" /></label>
                {canRoles && role !== 'super_admin' && (
                  <details className="border border-gray-200 rounded-xl p-3"><summary className="text-sm font-medium text-gray-800 cursor-pointer min-h-[28px]">Permissions personnalisées ({custom.length})</summary>
                    <div className="grid grid-cols-2 gap-1 mt-2">{allPermissions.map(p => <label key={p} className="flex items-center gap-2 text-xs text-gray-700 min-h-[28px]"><input type="checkbox" checked={custom.includes(p)} onChange={(e) => setCustom(e.target.checked ? [...custom, p] : custom.filter(x => x !== p))} className="accent-neon-pink w-4 h-4" data-testid={`custom-perm-${p}`} /><span className="font-mono">{p}</span></label>)}</div>
                  </details>
                )}
                <details className="border border-gray-200 rounded-xl p-3" data-testid="effective-permissions"><summary className="text-sm font-medium text-gray-800 cursor-pointer min-h-[28px]">Permissions effectives ({account.effective_permissions?.length || 0})</summary><div className="flex flex-wrap gap-1 mt-2">{(account.effective_permissions || []).map(p => <span key={p} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">{p}</span>)}</div></details>
                {(hasPermission('manage_team') || canRoles) && <Button onClick={save} disabled={busy || protectedTarget} className="w-full h-12 rounded-full bg-neon-pink text-gray-900 font-semibold" data-testid="account-save-btn">{busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}Enregistrer</Button>}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MemberAccountPanel;
